/**
 * PortfolioTab Component (TASK-407)
 *
 * Preview-first deck view that parallels BriefTab from TASK-406. By default
 * it shows the LLM-generated portfolio PPTX rendered as a PDF (via
 * BriefDeckPreview — content-agnostic). A "Charts" toggle reveals the
 * existing chart-based read-only view for advisors who want raw numbers
 * without waiting for a regeneration.
 *
 * Preview state (export, regenerating, language, view) is owned by
 * PreparationPage so it survives tab switches and matches the brief preview
 * lifecycle.
 */

import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  BarChart3,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  Globe,
  Loader2,
  PieChart,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import type { PortfolioOverview, Position, PerformanceMetrics, RiskMetrics } from '../../../services/api/portfolioApiService';
import {
  getPortfolioPptxDownloadHref,
  type ExportResponse,
  type PortfolioExportView,
} from '../../../services/api/exportApi';
import AllocationPieChart from '../../portfolio/molecules/AllocationPieChart';
import type { AllocationData } from '../../portfolio/molecules/AllocationPieChart';
import PerformanceChart from '../../portfolio/molecules/PerformanceChart';
import SimpleRiskMetrics from '../../portfolio/molecules/SimpleRiskMetrics';
import BriefDeckPreview from './BriefDeckPreview';

interface PortfolioTabProps {
  // Chart-mode props (pre-existing)
  overview: PortfolioOverview | null;
  positions: Position[];
  performanceMetrics?: PerformanceMetrics | null;
  performanceHistory?: Array<{ date: string; value: number; benchmark?: number }>;
  riskMetrics?: RiskMetrics | null;
  isLoading: boolean;
  error?: string | null;

  // Preview-mode props (TASK-407)
  clientId?: string;
  previewExport: ExportResponse | null;
  previewIsRegenerating: boolean;
  previewLanguage: 'en' | 'de' | 'fr';
  previewView: PortfolioExportView;
  onPreviewLanguageChange: (lang: 'en' | 'de' | 'fr') => void;
  onPreviewViewChange: (view: PortfolioExportView) => void;
  onPreviewRegenerate: () => void;
}

function getPrimaryCurrency(overview: PortfolioOverview | null): string {
  const byCurrency = overview?.allocation_by_currency;
  if (!byCurrency || typeof byCurrency !== 'object') return 'CHF';
  const entries = Object.entries(byCurrency);
  if (entries.length === 0) return 'CHF';
  entries.sort(([, a], [, b]) => (b as number) - (a as number));
  return entries[0][0];
}

function parseAllocation(raw: unknown): Record<string, number> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw as Record<string, number>;
}

type ViewMode = 'deck' | 'charts';

const LANGUAGES: Array<{ code: 'en' | 'de' | 'fr'; label: string }> = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
];

const PortfolioTab: React.FC<PortfolioTabProps> = ({
  overview,
  positions,
  performanceMetrics,
  performanceHistory,
  riskMetrics,
  isLoading,
  error,
  clientId,
  previewExport,
  previewIsRegenerating,
  previewLanguage,
  previewView,
  onPreviewLanguageChange,
  onPreviewViewChange,
  onPreviewRegenerate,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('deck');
  const [showAllHoldings, setShowAllHoldings] = useState(false);

  const currency = useMemo(() => getPrimaryCurrency(overview), [overview]);
  const allocationMap = useMemo(
    () => parseAllocation(overview?.allocation_by_asset_class),
    [overview],
  );
  const totalValue = overview?.total_value ?? 0;
  const healthScore = overview?.health_score ?? 0;
  const allocationData: AllocationData[] = useMemo(
    () => Object.entries(allocationMap)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value })),
    [allocationMap],
  );
  const displayedPositions = showAllHoldings ? positions : positions.slice(0, 10);

  // TASK-407: prefer the presigned MinIO URL directly (avoids the Vite dev
  // proxy following the 302 redirect server-side to an unreachable host).
  const pdfUrl = previewExport?.pdf_url ?? null;
  const downloadHref = previewExport?.pptx_url
    ?? (previewExport?.export_id ? getPortfolioPptxDownloadHref(previewExport.export_id) : null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (!overview && positions.length === 0 && !previewExport) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          {error ? (
            <>
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-sm text-red-600">{error}</p>
            </>
          ) : (
            <>
              <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No portfolio data available</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="px-6 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">Portfolio Snapshot</h2>
            {clientId && (
              <span className="text-xs text-gray-500">
                {currency} {totalValue.toLocaleString('de-CH', { minimumFractionDigits: 0 })} · Health {healthScore}/100
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View toggle (deck vs charts) */}
            <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
              <button
                onClick={() => setViewMode('deck')}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                  viewMode === 'deck' ? 'bg-kielis-navy text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                Deck
              </button>
              <button
                onClick={() => setViewMode('charts')}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                  viewMode === 'charts' ? 'bg-kielis-navy text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Charts
              </button>
            </div>

            {viewMode === 'deck' && (
              <>
                {/* View (client | full) */}
                <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
                  <button
                    onClick={() => onPreviewViewChange('client')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                      previewView === 'client' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    Client
                  </button>
                  <button
                    onClick={() => onPreviewViewChange('full')}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                      previewView === 'full' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    Full
                  </button>
                </div>

                {/* Language */}
                <div className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1">
                  <Globe className="w-3 h-3 text-gray-400" />
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => onPreviewLanguageChange(lang.code)}
                      className={`px-1.5 text-xs font-medium rounded transition-colors ${
                        previewLanguage === lang.code ? 'text-kielis-navy font-bold' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Regenerate */}
                <button
                  onClick={onPreviewRegenerate}
                  disabled={previewIsRegenerating}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {previewIsRegenerating ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  Regenerate
                </button>

                {/* Download */}
                {downloadHref && (
                  <a
                    href={downloadHref}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded bg-kielis-navy text-white hover:bg-kielis-blue-dark"
                  >
                    <Download className="w-3.5 h-3.5" />
                    .pptx
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'deck' ? (
          <BriefDeckPreview
            pdfUrl={pdfUrl}
            isRegenerating={previewIsRegenerating}
            emptyState={
              <div className="flex flex-col items-center justify-center h-full text-center px-8">
                <Briefcase className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm text-gray-600 font-medium">No deck yet</p>
                <p className="text-xs text-gray-500 mt-1 max-w-xs">
                  Click <span className="font-medium">Regenerate</span> to produce a branded portfolio deck for this client.
                </p>
              </div>
            }
          />
        ) : (
          <div className="h-full overflow-y-auto p-6 space-y-6">
            {(overview || riskMetrics || performanceMetrics) && (
              <SimpleRiskMetrics
                annualizedReturn={performanceMetrics?.annualized_return ?? 0}
                volatility={riskMetrics?.volatility ?? overview?.volatility ?? 0}
                sharpeRatio={riskMetrics?.sharpe_ratio ?? overview?.sharpe_ratio ?? 0}
                activeShare={0}
                maxDrawdown={performanceMetrics?.max_drawdown ?? 0}
                additionalMetrics={{ beta: riskMetrics?.beta ?? undefined }}
              />
            )}

            {allocationData.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <PieChart className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Asset Allocation</h3>
                </div>
                <AllocationPieChart
                  data={allocationData}
                  height={220}
                  showLegend
                  showTooltip
                  innerRadius={50}
                />
              </div>
            )}

            {performanceHistory && performanceHistory.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Performance</h3>
                </div>
                <PerformanceChart
                  data={performanceHistory}
                  height={200}
                  showBenchmark={performanceHistory.some((d) => d.benchmark != null)}
                  chartType="area"
                  valueFormat="percentage"
                />
              </div>
            )}

            {positions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-800">Holdings ({positions.length})</h3>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Name</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Value</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">P&amp;L</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Weight</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {displayedPositions.map((pos) => {
                        const weight = totalValue > 0 ? (pos.market_value / totalValue) * 100 : 0;
                        const plPct = pos.book_value > 0 ? ((pos.unrealized_pl / pos.book_value) * 100) : 0;
                        return (
                          <tr key={pos.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{pos.isin}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[200px]">{pos.security_name}</div>
                            </td>
                            <td className="text-right px-3 py-2 text-gray-700">
                              {pos.market_value.toLocaleString('de-CH', { minimumFractionDigits: 0 })}
                            </td>
                            <td className={`text-right px-3 py-2 ${plPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {plPct >= 0 ? '+' : ''}{plPct.toFixed(1)}%
                            </td>
                            <td className="text-right px-3 py-2 text-gray-500">{weight.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {positions.length > 10 && (
                  <button
                    onClick={() => setShowAllHoldings(!showAllHoldings)}
                    className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showAllHoldings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showAllHoldings ? 'Show less' : `Show all ${positions.length} holdings`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioTab;
